import { useMemo } from 'react';
import prettyBytes from 'pretty-bytes';

import {
  RyeconClock,
  RyeconFileBlank,
  RyeconTable,
  RyeconTableRow,
} from '@dbt-labs/sourdough';

import DetailTabs, { TabInfo, TabType } from '@/components/DetailTabs';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useSnapshot } from '@/hooks/dbtStrategy/useSnapshot';
import { GenericResourceNode } from '@/hooks/discovery/types';
import { useValidatedParams } from '@/hooks/params';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { isDevelopment, UrlEnvironmentType } from '@/typings/url';
import { getDatabaseObjectType } from '@/util/database';
import { formatAbsoluteLocalDate, formatDateCommon } from '@/util/date';
import { useWarehouseHeaderIcon } from '../../hooks/useWarehouseHeaderIcon';
import {
  getResourceTypeHeaderIcon,
  ResourceDetailsHeader,
  TextIconPair,
} from '../Header';
import { ExecutionInfo } from '../Sections/RecentSection';
import { CodeView } from '../Tabs/CodeView';
import { ColumnsView } from '../Tabs/ColumnsView';
import { GeneralView } from '../Tabs/GeneralView';
import { PerformanceView } from '../Tabs/PerformanceView';

const useTabs = (environmentType: UrlEnvironmentType, enablePerformance: boolean) => {
  return useMemo(() => {
    const tabs: TabInfo[] = [
      { type: 'general' },
      { type: 'code' },
      { type: 'columns' },
      ...(isDevelopment(environmentType) || !enablePerformance
        ? []
        : [{ type: 'performance' as TabType }]),
    ];
    return tabs;
  }, [enablePerformance, environmentType]);
};

type ResourceParams = {
  uniqueId: ParsedUniqueId;
};

export const SnapshotView = ({ uniqueId }: ResourceParams) => {
  const { hasAccountFeature } = useDbtCloudAccount();
  const { environmentType } = useValidatedParams();
  const snapshotQuery = useSnapshot(uniqueId);
  const tabs = useTabs(environmentType, hasAccountFeature('explorerModelPerformance'));

  const warehouseHeaderIcon = useWarehouseHeaderIcon({
    uniqueId,
    environmentType,
  });

  if (snapshotQuery.error) return <>{snapshotQuery.error.stack}</>;
  const snapshot = snapshotQuery.fetching ? null : snapshotQuery.data;
  const date = snapshot?.applied?.executionInfo?.executeCompletedAt
    ? new Date(snapshot.applied.executionInfo.executeCompletedAt)
    : undefined;
  const updatedAt = date && formatAbsoluteLocalDate(date);
  const updatedAtUtc = date && formatDateCommon(date).utc;
  const objectType =
    snapshot?.applied?.catalog?.type &&
    getDatabaseObjectType(snapshot.applied?.catalog.type);
  const rowCount = snapshot?.applied?.catalog?.rowCountStat ?? undefined;
  const size = snapshot?.applied?.catalog?.bytesStat ?? undefined;
  const sizeStr = size && prettyBytes(size);
  const headerIcons: TextIconPair[] = [getResourceTypeHeaderIcon('snapshot')];
  if (updatedAt) {
    headerIcons.push({
      ryecon: RyeconClock,
      text: `Last run ${updatedAt}`,
      tooltip: updatedAtUtc || undefined,
    });
  }
  if (objectType) {
    headerIcons.push({
      ryecon: RyeconTable,
      text: `${objectType}`,
    });
  }
  if (warehouseHeaderIcon) {
    headerIcons.push(warehouseHeaderIcon);
  }
  if (rowCount) {
    headerIcons.push({
      ryecon: RyeconTableRow,
      text: `${rowCount.toLocaleString()} rows`,
    });
  }
  if (sizeStr) {
    headerIcons.push({
      ryecon: RyeconFileBlank,
      text: `${sizeStr}`,
    });
  }

  return (
    <>
      <ResourceDetailsHeader
        uniqueId={uniqueId.raw}
        title={uniqueId.remainder}
        headerIcons={headerIcons}
        packageName={uniqueId.packageName}
        files={[snapshot?.applied?.patchPath, snapshot?.applied?.filePath]}
        name={snapshot?.applied?.name}
        resourceType="snapshot"
        updatedDate={date}
      />
      <DetailTabs tabs={tabs} show={!!snapshot}>
        {(activeTab) => {
          return (
            <>
              {activeTab === 'general' && (
                <GeneralView
                  resource={snapshot?.applied as GenericResourceNode}
                  uniqueId={uniqueId}
                  description={snapshot?.applied?.description}
                  meta={snapshot?.applied?.meta}
                />
              )}
              {activeTab === 'code' && (
                <CodeView rawCode={snapshot?.applied?.rawCode} />
              )}
              {activeTab === 'columns' && snapshot?.applied && (
                <ColumnsView resource={snapshot.applied} />
              )}
              {activeTab === 'performance' && (
                <PerformanceView
                  uniqueId={uniqueId.raw}
                  executionInfo={
                    snapshot?.applied?.executionInfo as ExecutionInfo | null | undefined
                  }
                  resourceType="snapshot"
                />
              )}
            </>
          );
        }}
      </DetailTabs>
    </>
  );
};
